import { Carousel2, ChannelCard } from '@intigral/ui';
import { SectionHeaderWithPagination } from '@intigral/ui/components/SectionHeaderWithPagination';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { COMPONENT_TYPES } from '../../config/constants';
import { EVENT, KEYS } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import h from '../../helpers/common';
import f from '../../helpers/formatter';
import f2 from '../../helpers/formatter2';
import url from '../../helpers/url';
import useRewindDuration from '../../hooks/useRewindDuration';
import { showUpgradedModal } from '../../store/reducers/generalConfigurationSlice';
import { IStore } from '../../interfaces/StoreTypes';
import { EmptyData, Text } from '@intigral/shared';
import { __ } from '../../translations';
import { setSession } from '@intigral/utils/lib/browser-storage/localStorage';

import useLanguage from '../../hooks/useLanguage';
import usePPVConfig from '../../hooks/usePPVConfig';
import usePPVManagement from '../../hooks/usePPVManagement';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { showGuestPromotionPopup } from '../../config/guestMode';
import { userAccessControl } from '../../utils/userAccessControl';

interface IMixpanelContext {
    track?: (name: string, value: any) => void;
}

interface ChannelsCarouselProps {
    onSeeAllClick?: () => void;
    seeAllText?: string;
    seeAllUrl?: string;
    onChannelClick?: (item: any, index: number) => void;
    meta?: {
        channel_posters?: any[];
        title?: any;
    };
    content?: any[];
    params?: {
        items_count: number;
    };
    carousel?: {
        feedType?: string;
        id?: string;
        type?: string;
    };
    id?: string;
    data_stream?: string;
    componentType?: string;
}

export const ChannelsCarousel: React.FC<ChannelsCarouselProps> = (props) => {
    const { onSeeAllClick = undefined, seeAllText = '', seeAllUrl = undefined, onChannelClick = undefined } = props;
    //  This section is only for Div vod carousel ,
    //  if Guid is not present for promotions and need to show Div contents,then getting guid from all assets/series apis and pass it div apis.
    const [liveTvChannels, setLiveTvChannels] = useState<any[]>([]);
    const [failed, setFailed] = useState(false);
    const [emptyPlaceholder, setEmptyPlaceholder] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userChannels = useSelector((state: IStore) => state.liveTv.userChannels.channels) ?? [];
    // Using React Query instead of Redux for configuration
    const { channelImage } = useConfigurationSections();
    const channelImageId = channelImage?.id || 'colour_264x26';

    useEffect(() => {
        async function fetchData() {
            try {
                const {
                    params: { items_count },
                    carousel = {},
                } = props;
                const { feedType = null } = carousel;

                if (props?.content?.length === 0 && feedType === 'most_watched_channels') {
                    setEmptyPlaceholder(true);
                    return;
                }

                const channelsHistory = props.content;

                const slicedChannelsHistory = channelsHistory.slice(0, items_count);
                setLiveTvChannels(slicedChannelsHistory);

                /* Need to consider the below cases for fetching the channels
                   ------------------------------------------------------------
                    (1) -> RESOLVED
                */

                // if (data_stream === CHANNEL_TYPES.USER_CHANNEL_HISTORY) { (1)
                //     const channelsHistory = props.content;
                //     const slicedChannelsHistory = channelsHistory.slice(0, items_count);
                //     setLiveTvChannels(slicedChannelsHistory);
                // } else if (data_stream === CHANNEL_TYPES.STATIC_CAROUSEL) { (2)
                //     const channels = await apis.getJawwyService().channels().getChannels();
                //     const currentGuids = f2.pluck(content, 'guid');
                //     const filteredChannels = channels.filter((channel) => currentGuids.includes(channel.guid)) ?? [];
                //     setLiveTvChannels(filteredChannels);
                // } else if (data_stream === CHANNEL_TYPES.EMBED_CHANNELS) { (3)
                //     setLiveTvChannels(props.channels);
                // }
            } catch (e) {
                setFailed(false);
            }
        }

        fetchData();
    }, [props.content]);

    /* -------------------*/
    const { track } = useContext(MixpanelContext) as IMixpanelContext;
    const componentId = props && props.id ? props.id : '';
    const language = useLanguage();

    // Load PPV configuration to avoid race condition on first channel click
    const { isLoading: isPPVConfigLoading } = usePPVConfig();

    // Use PPV management hook for all PPV-related functionality
    const {
        detectChannelPPV,
        shouldShowLock,
        getPPVEventForChannel,
        isPPVEventPurchased,
        ppvConfigLoaded,
        enableLockIcon,
    } = usePPVManagement();

    const handleItemClick = (item: any) => {
        const { screenName, menuName } = h.getScreenMixpanelDataById(item);

        const eventProps = {
            [KEYS.CAROUSEL.SCREEN_NAME]: h.getScreen(),
            [KEYS.CAROUSEL.CAROUSEL_NAME]: props?.meta?.title,
            [KEYS.CAROUSEL.CAROUSEL_TYPE]: h.getCarouselComponentType(COMPONENT_TYPES.REWIND_CAROUSEL),
        };

        track?.(`${EVENT.PROVIDER.PROVIDER_ITEM_CLICK}`, {
            [KEYS.GENERAL.PROVIDER_NAME]: menuName || screenName,
            ...eventProps,
        });
    };

    const handleChannelClick = (event: React.MouseEvent, item: any, index: number, retryCount = 0) => {
        event.preventDefault();

        if (props.componentType === COMPONENT_TYPES.SEARCH_RESULTS && showGuestPromotionPopup(event)) {
            return;
        }

        const channelId = item?.channelID || item?.channelId || item?.id;

        setSession('noAds', 'true');

        // Use PPV management hook for enhanced channel detection
        const { isLocked, enhancedChannel } = shouldShowLock(item, channelId);

        if (enhancedChannel?.isPPV) {
            // PPV config should be loaded by usePPVConfig hook, but add fallback protection
            if (!ppvConfigLoaded) {
                // If PPV config is still loading, wait briefly and retry once
                if (retryCount < 1 && isPPVConfigLoading) {
                    setTimeout(() => {
                        const retryEvent = { ...event, preventDefault: () => void 0 };
                        handleChannelClick(retryEvent as any, item, index, retryCount + 1);
                    }, 500);
                    return;
                } else {
                    // PPV config failed to load or not available, fall back to regular LiveTV navigation
                    navigate(
                        url.route('livetv', {
                            channelId: btoa(channelId),
                        })
                    );
                    return;
                }
            }

            const ppvEvent = getPPVEventForChannel(enhancedChannel);
            const isPurchased = isPPVEventPurchased(ppvEvent);

            if (isPurchased) {
                // Scenario 1: PPV channel is purchased - go to LiveTV
                navigate(
                    url.route('livetv', {
                        channelId: btoa(channelId),
                    })
                );
            } else if (ppvEvent?.eventId) {
                // Scenario 2: PPV channel not purchased - go to PPV Modal Details
                const currentLang = h.getLang ? h.getLang() : 'en';
                navigate(`/${currentLang}?eventId=${ppvEvent.eventId}`);
                return;
            } else {
                // Fallback: if no PPV event found, go to LiveTV
                navigate(
                    url.route('livetv', {
                        channelId: btoa(channelId),
                    })
                );
                return;
            }
        } else {
            // Non-PPV channel - go to LiveTV (current behavior)
            // If locked/premium, show upgrade modal and DO NOT redirect.
            if (isLocked) {
                if (enableLockIcon) {
                    dispatch(showUpgradedModal(true));
                }
                return;
            }

            navigate(
                url.route('livetv', {
                    channelId: btoa(channelId),
                })
            );
            return;
        }

        // Call the original click handlers
        if (onChannelClick) {
            onChannelClick(item, index);
        } else {
            handleItemClick(
                url.route('livetv', {
                    channelId: btoa(channelId),
                })
            );
        }
    };

    if (failed) return null;

    const swiperSettings = {
        autoplay: false,
        freeMode: false,
        loop: false,
        slidesPerGroup: 1,
        slidesPerView: 3,
        dir: language === 'ar' ? 'rtl' : 'ltr',
        breakpoints: {
            560: {
                slidesPerView: 4,
            },
            924: {
                slidesPerView: 5,
            },
            1212: {
                slidesPerView: 7,
            },
            1500: {
                slidesPerView: 9,
            },
        },
    };

    return (
        <>
            {emptyPlaceholder && ( // This to show the empty placeholder if there is no data
                <div
                    className="container full-width providers-gallery channels-slider"
                    id={componentId}
                    style={{ direction: language === 'ar' ? 'rtl' : 'ltr', overflow: 'hidden', position: 'relative' }}
                    data-test-id={props?.carousel?.id}
                >
                    <SectionHeaderWithPagination
                        title={f.getComponentTitle(props.meta)}
                        className="paging"
                        onClick={onSeeAllClick}
                        seeAllText={seeAllText}
                        seeAllUrl={seeAllUrl}
                    />

                    <div className="full-width">
                        <EmptyData>
                            <Text as="p" margin="0" size={{ default: '14', md: '20' }}>
                                {__('no_mostwatched_data')}
                            </Text>
                        </EmptyData>
                    </div>
                </div>
            )}

            {!emptyPlaceholder && liveTvChannels && liveTvChannels.length > 0 && (
                <div
                    className="container full-width providers-gallery channels-slider"
                    id={componentId}
                    style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
                    data-test-id={props?.carousel?.id}
                >
                    <SectionHeaderWithPagination
                        title={f.getComponentTitle(props.meta)}
                        className="paging"
                        onClick={onSeeAllClick}
                        seeAllText={seeAllText}
                        seeAllUrl={seeAllUrl}
                    />
                    <div className="provider-slider genres-slider">
                        <Carousel2 swiperSettings={swiperSettings}>
                            {liveTvChannels
                                .filter((item) => userAccessControl.canAccessChannel(item))
                                .map((item, index) => {
                                    const channelId = item?.channelID || item?.channelId || item?.id;

                                    // Use the PPV management hook for all PPV-related logic
                                    const { isLocked, enhancedChannel } = shouldShowLock(item, channelId);

                                    let logoSrc = '';
                                    const channelImage = enhancedChannel?.channelImages?.find(
                                        (img) => img.id === channelImageId
                                    );
                                    logoSrc = channelImageId.toLowerCase().includes('svg')
                                        ? channelImage?.url || ''
                                        : h.getImagizerUrl(channelImage?.url || '', 176);

                                    return (
                                        <React.Fragment key={`channels-${channelId}`}>
                                            <Link
                                                to="#"
                                                onClick={(event) => handleChannelClick(event, item, index)}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    opacity: isPPVConfigLoading ? 0.7 : 1,
                                                    pointerEvents: isPPVConfigLoading ? 'none' : 'auto',
                                                }}
                                            >
                                                <ChannelCard
                                                    index={channelId}
                                                    logoSrc={logoSrc}
                                                    channelNo={item.channelNumber}
                                                    bottomLabel={useRewindDuration(item)}
                                                    isLocked={isLocked}
                                                    onClickLock={() => {
                                                        if (enhancedChannel?.isPPV) {
                                                            return;
                                                        }
                                                        if (isLocked && !enhancedChannel?.isPPV) {
                                                            dispatch(showUpgradedModal(true));
                                                        }
                                                    }}
                                                    isSelected={false}
                                                    onChannelSelect={() => {
                                                        return;
                                                    }}
                                                    title={''}
                                                    ratio={2.5}
                                                />
                                            </Link>
                                        </React.Fragment>
                                    );
                                })}
                        </Carousel2>
                    </div>
                </div>
            )}
        </>
    );
};
